import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STEP_TESTS_FEATURE_FLAG } from "../../shared/featureFlagNames";
import { sharedMutators } from "../../shared/mutators";
import { cpuTimeMs, peakMemoryBytes } from "../../shared/resourceUsage";
import {
  nextStepTestName,
  stepTestNamesFromFiles,
} from "../../shared/stepTests/constants";
import { useExecutionContent } from "../hooks/useExecutionContent";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import {
  useInFlightStepTestCapture,
  useStepExecutions,
} from "../hooks/useStepExecutions";
import type { StepTestFixtureSetupRequest } from "../hooks/useStepTestPin";
import { formatBytes } from "../lib/formatBytes";
import { pinContentIssue } from "../lib/pinEligibility";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Button } from "./Button";
import {
  CodeEditor,
  type CodeEditorHandle,
  type CodeSelection,
  type PinnedRange,
} from "./CodeEditor";
import { useCodeEditorSearch } from "./CodeEditor/useCodeEditorSearch";
import { Dropdown } from "./Dropdown";
import { EmptyState } from "./EmptyState";
import { ExecutionContent } from "./ExecutionContent";
import type { FileRenderControl } from "./FileRender";
import {
  ToggleButton,
  ToggleGroup,
  ToggleIcon,
  ToggleIconContainer,
} from "./HeadingToggle";
import type { IconName } from "./Icon";
import { Icon } from "./Icon";
import { RequestsList } from "./RequestsList";
import { ShortcutHint } from "./ShortcutHint";
import { StepDetails } from "./StepDetails";
import { StepTests } from "./StepTests";
import { StepTestStatusDot } from "./StepTestStatusDot";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WavyLine } from "./WavyLine";
import type { CodeSnippet, StepData } from "./Workflow";

const animalIcons: IconName[] = ["rabbit", "squirrel", "snail", "bird", "rat"];

export const formatTime = (time: number) => new Date(time).toLocaleString();

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

const iconShown = { opacity: 1, transform: "rotate(0deg) scale(1)" };
const iconHiddenCw = { opacity: 0, transform: "rotate(90deg) scale(0.5)" };
const iconHiddenCcw = { opacity: 0, transform: "rotate(-90deg) scale(0.5)" };

const HeadingOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 4px 8px;
  background: transparent;
  border-radius: 9999px;

  &:hover {
    background: var(--alpha4);
  }
`;

const MetadataList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetadataItem = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogOutput = styled("div")`
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const BuildLogsButton = styled(Button)`
  &&& {
    background: var(--alpha4);
  }
  &&&:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

const PinLabel = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

type Props = {
  step: StepData;
  branchId: string;
  commitId: string;
  files: readonly { path: string }[];
  isMainBranch: boolean;
  pinnedByExecId?: Map<string, string>;
  onSelectionChange?: (selections: CodeSelection[]) => void;
  codeSnippets?: readonly CodeSnippet[];
  isBeingWritten?: boolean;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest?: (request: StepTestFixtureSetupRequest) => void;
};

export function StepExecutions({
  step,
  branchId,
  commitId,
  files,
  isMainBranch,
  pinnedByExecId,
  onSelectionChange,
  codeSnippets,
  isBeingWritten,
  onInvestigateFailure,
  onAskSetupStepTest,
}: Props) {
  const {
    workflowId,
    execution: selectedExecutionId,
    view,
  } = useCurrentUrl(routes.space.workflow);
  const stepTestsEnabled = useFeatureFlag(STEP_TESTS_FEATURE_FLAG);
  const selectedView =
    !stepTestsEnabled && view === "stepTests" ? "output" : (view ?? "output");
  const setSelectedView = (v: string) =>
    navigate(null, { view: v }, { replace: true });

  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);

  // `files` paths are already step-relative — selectStepFiles strips the step
  // prefix upstream — so match the step-test files directly, without re-stripping.
  const stepTestNames = useMemo(() => stepTestNamesFromFiles(files), [files]);

  useEffect(() => {
    const views: Record<string, string> = {
      Digit1: "output",
      Digit2: "input",
      Digit3: "logs",
      Digit4: "metadata",
      Digit6: "requests",
    };
    if (stepTestsEnabled) views.Digit5 = "stepTests";
    function handleKeyDown(e: KeyboardEvent) {
      if (!e.altKey || !(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      const v = views[e.code];
      if (v) {
        e.preventDefault();
        setSelectedView(v);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [stepTestsEnabled]);

  const [animalIcon] = useState(
    () =>
      animalIcons[Math.floor(Math.random() * animalIcons.length)] ?? "rabbit"
  );
  const { executions } = useStepExecutions(workflowId, branchId, step.id);
  const inFlightCapture = useInFlightStepTestCapture(
    workflowId,
    branchId,
    stepTestsEnabled && !isMainBranch ? step.id : null
  );
  const isCapturingFixtures = inFlightCapture != null;
  const selectedExecution =
    executions.find((execution) => execution.id === selectedExecutionId) ??
    executions[0];
  const fullOutput = useExecutionContent(
    selectedExecution,
    "output",
    selectedView === "output" || selectedView === "metadata"
  );
  const fullInput = useExecutionContent(
    selectedExecution,
    "input",
    selectedView === "input" || selectedView === "output"
  );
  const fullLog = useExecutionContent(
    selectedExecution,
    "log",
    selectedView === "logs" || selectedView === "metadata"
  );
  const fullRequests = useExecutionContent(
    selectedExecution,
    "requests",
    selectedView === "requests"
  );
  const activeContent =
    selectedView === "output"
      ? fullOutput
      : selectedView === "input"
        ? fullInput
        : undefined;
  const pendingBuild = executions.find(
    (e) => e.kind === "build" && e.status === "pending"
  );
  const isWaitingForBuild =
    selectedExecution?.kind === "run" &&
    selectedExecution?.status === "pending" &&
    pendingBuild != null;
  const buildForSelectedRun =
    selectedExecution?.kind === "run"
      ? executions.find(
          (e) =>
            e.kind === "build" && e.createdAt <= selectedExecution.createdAt
        )
      : undefined;
  const failedBuild =
    selectedExecution?.status === "error" &&
    buildForSelectedRun?.status === "error"
      ? buildForSelectedRun
      : undefined;
  const goToFailedBuild = failedBuild
    ? () =>
        navigate(
          null,
          { execution: failedBuild.id, view: "logs" },
          { replace: true }
        )
    : undefined;

  const logEditorRef = useRef<CodeEditorHandle | null>(null);
  const stickToBottomRef = useRef(true);
  const search = useCodeEditorSearch(selectedView);

  const logPinnedRanges = useMemo<PinnedRange[] | undefined>(() => {
    const expected = `${step.name}/log.txt`;
    const matching = codeSnippets?.filter((s) => s.filePath === expected);
    return matching?.length
      ? matching.map((s) => ({ from: s.from, to: s.to }))
      : undefined;
  }, [codeSnippets, step.name]);
  const isStreaming =
    selectedExecution?.status === "pending" && !!fullLog.content;
  const logContent = fullLog.content;
  useEffect(() => {
    if (!isStreaming || !logContent) return;
    if (stickToBottomRef.current) logEditorRef.current?.scrollToBottom();
  }, [isStreaming, logContent]);

  const requestsEndRef = useRef<HTMLDivElement>(null);
  const isRequestsStreaming =
    selectedExecution?.status === "pending" && !!fullRequests.content;
  const requestsContent = fullRequests.content;
  useEffect(() => {
    if (isRequestsStreaming && requestsContent) {
      requestsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isRequestsStreaming, requestsContent]);

  const [fileControls, setFileControls] = useState<FileRenderControl[]>([]);

  const copyableContent =
    selectedView === "output" || selectedView === "input"
      ? activeContent?.content
      : selectedView === "logs"
        ? fullLog.content
        : selectedView === "requests"
          ? fullRequests.content
          : undefined;
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const copyToClipboard = useCallback(() => {
    if (!copyableContent) return;
    setCopied(true);
    clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => setCopied(false), 1500);
    void writeToClipboard(copyableContent);
  }, [copyableContent]);

  // The pin button needs the run’s input+output, which only load on the output
  // view, so show it there — enabled when the output is plain text/JSON,
  // otherwise disabled with the reason — rather than silently hiding it.
  const pinRun =
    stepTestsEnabled &&
    !isMainBranch &&
    selectedView === "output" &&
    selectedExecution?.kind === "run" &&
    selectedExecution.status === "success"
      ? selectedExecution
      : null;
  const issue = pinContentIssue(fullOutput, fullInput);
  const canPin = pinRun != null && issue === null;
  const pinnedName = pinRun ? (pinnedByExecId?.get(pinRun.id) ?? null) : null;
  const isPinned = pinnedName !== null;
  const showCapturing = isCapturingFixtures && isPinned;
  const pinTooltip = showCapturing
    ? "Capturing this step test’s output and volume fixtures…"
    : isPinned
      ? "Unpin this run’s step test. Future checks will no longer replay this input."
      : (issue ??
        "Pin this run’s input and output as a step test. Future checks replay the input and expect the same output.");
  const viewItems = [
    {
      type: "button" as const,
      label: "Output",
      left: <Icon name="output" size={16} />,
      right:
        selectedView === "output" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥1</ShortcutHint>
        ),
      onClick: () => setSelectedView("output"),
    },
    {
      type: "button" as const,
      label: "Input",
      left: <Icon name="input" size={16} />,
      right:
        selectedView === "input" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥2</ShortcutHint>
        ),
      onClick: () => setSelectedView("input"),
    },
    {
      type: "button" as const,
      label: "Logs",
      left: <Icon name="logs" size={16} />,
      right:
        selectedView === "logs" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥3</ShortcutHint>
        ),
      onClick: () => setSelectedView("logs"),
    },
    {
      type: "button" as const,
      label: "Metadata",
      left: <Icon name="info" size={16} />,
      right:
        selectedView === "metadata" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥4</ShortcutHint>
        ),
      onClick: () => setSelectedView("metadata"),
    },
    ...(stepTestsEnabled
      ? [
          {
            type: "button" as const,
            label: "Step tests",
            left: <Icon name="list-todo" size={16} />,
            right:
              selectedView === "stepTests" ? (
                <Icon name="check" size={12} color="var(--text-secondary)" />
              ) : (
                <ShortcutHint>⌘⌥5</ShortcutHint>
              ),
            onClick: () => setSelectedView("stepTests"),
          },
        ]
      : []),
    {
      type: "button" as const,
      label: "Requests",
      left: <Icon name="arrow-up-right" size={16} />,
      right:
        selectedView === "requests" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥6</ShortcutHint>
        ),
      onClick: () => setSelectedView("requests"),
    },
  ];

  return (
    <search.Provider>
      <StepDetails
        subheader={search.subheader}
        headingRight={
          <>
            {pinRun && (
              <Tooltip content={pinTooltip}>
                <Button
                  variant="secondary"
                  disabled={!isPinned && !canPin}
                  onClick={() => {
                    if (isPinned) {
                      void navigateOnBranchChange((branchParams) =>
                        z.mutate(
                          sharedMutators.unpinStepTest({
                            workflowId,
                            ...branchParams,
                            stepDir: step.id,
                            name: pinnedName,
                          })
                        )
                      );
                    } else if (step.hasStorage && onAskSetupStepTest) {
                      onAskSetupStepTest({
                        stepDir: step.id,
                        executionId: pinRun.id,
                        name: nextStepTestName(stepTestNames),
                      });
                    } else {
                      void navigateOnBranchChange((branchParams) =>
                        z.mutate(
                          sharedMutators.pinStepTest({
                            workflowId,
                            ...branchParams,
                            stepDir: step.id,
                            executionId: pinRun.id,
                            name: nextStepTestName(stepTestNames),
                            input: fullInput.content ?? "",
                            expectedOutput: fullOutput.content ?? "",
                          })
                        )
                      );
                    }
                  }}
                >
                  <PinLabel>
                    {showCapturing && <StepTestStatusDot status="running" />}
                    {isPinned ? "Unpin as step test" : "Pin as step test"}
                  </PinLabel>
                </Button>
              </Tooltip>
            )}
            <ToggleGroup>
              {copyableContent && (
                <Tooltip content={copied ? "Copied" : "Copy"}>
                  <ToggleButton onPress={copyToClipboard}>
                    <ToggleIconContainer>
                      <ToggleIcon style={copied ? iconHiddenCw : iconShown}>
                        <Icon name="copy" size={12} />
                      </ToggleIcon>
                      <ToggleIcon style={copied ? iconShown : iconHiddenCcw}>
                        <Icon name="check" size={12} />
                      </ToggleIcon>
                    </ToggleIconContainer>
                  </ToggleButton>
                </Tooltip>
              )}
              {search.toggleButton}
              {fileControls.map((control) => (
                <Tooltip key={control.id} content={control.label}>
                  <ToggleButton
                    onPress={control.onToggle}
                    aria-pressed={control.active}
                    data-icon-crossfade={control.activeIcon ? true : undefined}
                  >
                    {control.activeIcon ? (
                      <ToggleIconContainer>
                        <ToggleIcon
                          style={control.active ? iconHiddenCw : iconShown}
                        >
                          <Icon name={control.icon} size={12} />
                        </ToggleIcon>
                        <ToggleIcon
                          style={control.active ? iconShown : iconHiddenCcw}
                        >
                          <Icon name={control.activeIcon} size={12} />
                        </ToggleIcon>
                      </ToggleIconContainer>
                    ) : (
                      <Icon name={control.icon} size={12} />
                    )}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleGroup>
          </>
        }
        heading={
          <Dropdown
            align="bottom-left"
            opener={
              <HeadingOpener>
                <span style={{ display: "flex" }}>
                  {selectedView === "output" ? (
                    <Icon name="output" size={12} />
                  ) : selectedView === "input" ? (
                    <Icon name="input" size={12} />
                  ) : selectedView === "logs" ? (
                    <Icon name="logs" size={12} />
                  ) : selectedView === "requests" ? (
                    <Icon name="arrow-up-right" size={12} />
                  ) : selectedView === "stepTests" ? (
                    <Icon name="list-todo" size={12} />
                  ) : (
                    <Icon name="info" size={12} />
                  )}
                </span>
                <Text variant="text-small-md">
                  {selectedView === "output"
                    ? "Output"
                    : selectedView === "input"
                      ? "Input"
                      : selectedView === "logs"
                        ? "Logs"
                        : selectedView === "requests"
                          ? "Requests"
                          : selectedView === "stepTests"
                            ? "Step tests"
                            : "Metadata"}
                </Text>
                <Icon name="chevron-down" size={12} />
              </HeadingOpener>
            }
            sections={[
              {
                label: "View",
                items: viewItems,
              },
            ]}
          />
        }
        body={
          stepTestsEnabled && selectedView === "stepTests" ? (
            <StepTests
              step={step}
              workflowId={workflowId}
              branchId={branchId}
              commitId={commitId}
              files={files}
              isMainBranch={isMainBranch}
              onInvestigateFailure={onInvestigateFailure}
            />
          ) : selectedView === "output" ? (
            selectedExecution?.kind === "build" &&
            selectedExecution.status === "pending" ? (
              <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
            ) : selectedExecution?.kind === "build" &&
              selectedExecution.status === "success" ? (
              <EmptyState
                iconName="package-2"
                iconSize={24}
                maxWidth={220}
                title="Built successfully"
                description="Dependencies are installed and up to date."
              />
            ) : isWaitingForBuild ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Waiting for build"
                description="This run will start once the build finishes."
              />
            ) : failedBuild ? (
              <EmptyState
                iconName="heart-crack"
                iconSize={24}
                maxWidth={220}
                title="Build failed"
                description="This step couldn't run because the build failed."
                action={
                  <BuildLogsButton
                    variant="secondary"
                    onClick={goToFailedBuild}
                  >
                    View build logs
                  </BuildLogsButton>
                }
              />
            ) : selectedExecution ? (
              <ExecutionContent
                execution={selectedExecution}
                onShowLogs={() => setSelectedView("logs")}
                stepName={step.name}
                publicRoute={step.publicRoute ?? undefined}
                onSelectionChange={onSelectionChange}
                codeSnippets={codeSnippets}
                onControls={setFileControls}
              />
            ) : isBeingWritten ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Taking shape"
                description="3B is still writing this step."
              />
            ) : (
              <EmptyState
                iconName={animalIcon}
                iconSize={24}
                maxWidth={220}
                title="No output yet"
                description="Run this step to see its output here."
              />
            )
          ) : selectedView === "input" ? (
            selectedExecution ? (
              <ExecutionContent
                field="input"
                execution={selectedExecution}
                stepName={step.name}
                onSelectionChange={onSelectionChange}
                codeSnippets={codeSnippets}
                onControls={setFileControls}
              />
            ) : (
              <EmptyState
                iconName={animalIcon}
                iconSize={24}
                maxWidth={220}
                title="No input"
                description="This step wasn't triggered with any input."
              />
            )
          ) : selectedView === "logs" ? (
            failedBuild ? (
              <EmptyState
                iconName="heart-crack"
                iconSize={24}
                maxWidth={220}
                title="Build failed"
                description="This step couldn't run because the build failed."
                action={
                  <BuildLogsButton
                    variant="secondary"
                    onClick={goToFailedBuild}
                  >
                    View build logs
                  </BuildLogsButton>
                }
              />
            ) : fullLog.content ? (
              <CodeEditor
                ref={logEditorRef}
                filePath="log.txt"
                value={fullLog.content}
                onSelectionChange={onSelectionChange}
                pinnedRanges={logPinnedRanges}
                onVisibleLineChange={(line) => {
                  stickToBottomRef.current =
                    line == null
                      ? true
                      : (logEditorRef.current?.isAtBottom() ?? true);
                }}
              />
            ) : fullLog.loading ? null : selectedExecution?.kind === "build" &&
              selectedExecution.status === "pending" ? (
              <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
            ) : isWaitingForBuild ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Waiting for build"
                description="This run will start once the build finishes."
              />
            ) : selectedExecution?.status === "pending" ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Running"
                description="Hang tight, logs will appear when this step runs."
              />
            ) : (
              <LogOutput>
                <Text variant="code-regular" color="var(--text-secondary)">
                  Nothing logged
                </Text>
              </LogOutput>
            )
          ) : selectedView === "requests" ? (
            failedBuild ? (
              <EmptyState
                iconName="heart-crack"
                iconSize={24}
                maxWidth={220}
                title="Build failed"
                description="This step couldn't run because the build failed."
                action={
                  <BuildLogsButton
                    variant="secondary"
                    onClick={goToFailedBuild}
                  >
                    View build logs
                  </BuildLogsButton>
                }
              />
            ) : fullRequests.content ? (
              <RequestsList
                content={fullRequests.content}
                executionId={selectedExecution?.id}
                bottomRef={requestsEndRef}
              />
            ) : fullRequests.loading ? null : selectedExecution?.kind ===
                "build" && selectedExecution.status === "pending" ? (
              <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
            ) : isWaitingForBuild ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Waiting for build"
                description="This run will start once the build finishes."
              />
            ) : selectedExecution?.status === "pending" ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Running"
                description="Hang tight, requests will appear when this step runs."
              />
            ) : (
              <LogOutput>
                <Text variant="code-regular" color="var(--text-secondary)">
                  No requests
                </Text>
              </LogOutput>
            )
          ) : selectedExecution && selectedExecution.status !== "pending" ? (
            <MetadataList>
              {selectedExecution.duration != null &&
                selectedExecution.completedAt != null && (
                  <MetadataItem>
                    <Text variant="text-regular" color="var(--text-secondary)">
                      Queue time
                    </Text>
                    <Text variant="text-regular">
                      {formatDuration(
                        selectedExecution.completedAt -
                          selectedExecution.createdAt -
                          selectedExecution.duration
                      )}
                    </Text>
                  </MetadataItem>
                )}
              {selectedExecution.duration != null && (
                <MetadataItem>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    Run time
                  </Text>
                  <Text variant="text-regular">
                    {formatDuration(selectedExecution.duration)}
                  </Text>
                </MetadataItem>
              )}
              {selectedExecution.result != null && (
                <>
                  <MetadataItem>
                    <Text variant="text-regular" color="var(--text-secondary)">
                      Peak memory
                    </Text>
                    <Text variant="text-regular">
                      {formatBytes(peakMemoryBytes(selectedExecution.result))}
                    </Text>
                  </MetadataItem>
                  <MetadataItem>
                    <Text variant="text-regular" color="var(--text-secondary)">
                      CPU time
                    </Text>
                    <Text variant="text-regular">
                      {formatDuration(cpuTimeMs(selectedExecution.result))}
                    </Text>
                  </MetadataItem>
                </>
              )}
              <MetadataItem>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Output size
                </Text>
                <Text variant="text-regular">
                  {fullOutput.size != null ? formatBytes(fullOutput.size) : "…"}
                </Text>
              </MetadataItem>
              <MetadataItem>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Log size
                </Text>
                <Text variant="text-regular">
                  {fullLog.size != null ? formatBytes(fullLog.size) : "…"}
                </Text>
              </MetadataItem>
              <MetadataItem>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Status
                </Text>
                <Text
                  variant="text-regular"
                  color={
                    selectedExecution.status === "success"
                      ? "var(--success)"
                      : "var(--error)"
                  }
                >
                  {selectedExecution.status}
                </Text>
              </MetadataItem>
            </MetadataList>
          ) : selectedExecution?.kind === "build" &&
            selectedExecution.status === "pending" ? (
            <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
          ) : isWaitingForBuild ? (
            <EmptyState
              icon={<WavyLine />}
              maxWidth={220}
              title="Waiting for build"
              description="This run will start once the build finishes."
            />
          ) : selectedExecution?.status === "pending" ? (
            <EmptyState
              icon={<WavyLine />}
              maxWidth={220}
              title="Running"
              description="Hang tight, metadata will appear when this step finishes."
            />
          ) : isBeingWritten ? (
            <EmptyState
              icon={<WavyLine />}
              maxWidth={220}
              title="Taking shape"
              description="3B is still writing this step."
            />
          ) : (
            <EmptyState
              iconName={animalIcon}
              iconSize={24}
              maxWidth={220}
              title="No output yet"
              description="Run this step to see its output here."
            />
          )
        }
      />
    </search.Provider>
  );
}
